import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { apiFetchJson } from '../utils/apiFetchPolicy';
import { searchIndex, flattenGroups, buildTerms, highlightParts, isRenderableImage } from '../utils/unifiedSearch';
import '../styles/SearchBar.css';

const DROPDOWN_LIMIT = 6;

const SEARCH_SITE = process.env.REACT_APP_SEARCH_SITE || 'omniversail';
const SEARCH_INDEX_BASE = process.env.REACT_APP_SEARCH_INDEX_BASE || '/data/search';
const SEARCH_FEDERATED = process.env.REACT_APP_SEARCH_FEDERATED !== '0';

/**
 * Navbar global search. This site's content (curriculum tree, competitions,
 * language catalogue) is committed JSON under `public/data/`, so the index is a
 * generated static JSON produced by `scripts/search`. See
 * src/utils/unifiedSearch.js for the client that scores it in the browser.
 */
export default function SearchBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hits, setHits] = useState(null);
  const [terms, setTerms] = useState([]);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const reqId = useRef(0);

  const runSearch = useCallback(async (q) => {
    const term = (q || '').trim();
    if (term.length < 2) {
      setHits(null);
      setFailed(false);
      setOpen(false);
      return;
    }
    const id = ++reqId.current;
    setLoading(true);
    setFailed(false);
    setTerms(buildTerms(term).map((x) => x.text));
    try {
      const [res, backend] = await Promise.all([
        searchIndex(term, { site: SEARCH_SITE, base: SEARCH_INDEX_BASE, limit: DROPDOWN_LIMIT, federated: SEARCH_FEDERATED }),
        apiFetchJson(`/api/search?q=${encodeURIComponent(term)}&limit=5`).catch(() => null),
      ]);
      if (id !== reqId.current) return;
      const flat = flattenGroups(res.groups, DROPDOWN_LIMIT);
      const social = [];
      for (const [type, list] of Object.entries(backend?.results || {})) {
        for (const h of list || []) {
          social.push({ id: `${type}:${h.id}`, title: h.title, text: h.subtitle, url: h.url, image: h.image, external: false, shardLabel: type, siteLabel: '' });
        }
      }
      const merged = [...flat, ...social].slice(0, DROPDOWN_LIMIT);
      setHits(merged);
      setFailed(merged.length === 0 && !!res.error);
      setOpen(true);
      setActive(-1);
    } catch {
      if (id !== reqId.current) return;
      setHits([]);
      setFailed(true);
      setOpen(true);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const tm = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(tm);
  }, [query, runSearch]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const go = (hit) => {
    setOpen(false);
    if (hit.external) {
      window.location.assign(hit.href || hit.url);
      return;
    }
    navigate(hit.url);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && active >= 0 && hits[active]) go(hits[active]);
      else if (query.trim().length >= 2) {
        setOpen(false);
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      }
      return;
    }
    if (!open || !hits || hits.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const renderTitle = (title) =>
    highlightParts(title, terms).map((part, i) =>
      part.hit ? (
        <mark key={i} className="nav-search-mark">
          {part.text}
        </mark>
      ) : (
        <span key={i}>{part.text}</span>
      ),
    );

  return (
    <div className="nav-search" ref={boxRef}>
      <div className="nav-search-input">
        <Search size={16} className="nav-search-icon" />
        <input
          type="text"
          value={query}
          placeholder={t('nav.search', 'Search the site…')}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => hits && setOpen(true)}
          onKeyDown={onKeyDown}
          aria-label={t('nav.search', 'Search')}
        />
        {query && (
          <button
            className="nav-search-clear"
            onClick={() => {
              setQuery('');
              setHits(null);
              setOpen(false);
            }}
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && query.trim().length >= 2 && (
        <div className="nav-search-dropdown">
          {loading && <div className="nav-search-empty">…</div>}
          {!loading && failed && <div className="nav-search-empty">{t('search.unavailable', 'Search is temporarily unavailable.')}</div>}
          {!loading && !failed && hits && hits.length === 0 && (
            <div className="nav-search-empty">{t('search.noResults', 'No results')}</div>
          )}
          {!loading && hits && hits.length > 0 && (
            <>
              {hits.map((h, idx) => (
                <button
                  key={h.id + idx}
                  className={`nav-search-item ${idx === active ? 'active' : ''}`}
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => go(h)}
                >
                  {isRenderableImage(h.image) && <img src={h.image} alt="" className="nav-search-thumb" />}
                  <span className="nav-search-item-text">
                    <span className="nav-search-item-title">{renderTitle(h.title)}</span>
                    <span className="nav-search-item-sub">
                      {h.external && h.siteLabel ? `${h.siteLabel} · ` : ''}
                      {h.shardLabel || ''}
                      {h.text ? ` — ${h.text.slice(0, 60)}` : ''}
                    </span>
                  </span>
                </button>
              ))}
              <button
                className="nav-search-all"
                onClick={() => {
                  setOpen(false);
                  navigate(`/search?q=${encodeURIComponent(query.trim())}`);
                }}
              >
                {t('search.viewAll', 'View all results')} →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
